// /onboarding/business/* — the business step as FIVE questions (board 25 v2
// business--name / --type / --city / --address / --phone): each answer fills
// something in the brand panel's live preview, so each gets its own screen
// while the counter stays ٤/٦ and the progress line grows per question.
// Nothing is written here — the hours screen creates the business; the
// answers live in the wizard's memory (localStorage draft). Server field
// errors from that write come back here through `location.state.fieldError`.

import { useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Search } from 'lucide-react';
import { PrimaryAction } from '@/components/Button';
import { TextInput } from '@/components/Form';
import { Skeleton } from '@/components/Skeleton';
import { sessionStore } from '@/lib/auth';
import { useConfigQuery } from '@/lib/config';
import { normalizeIraqiPhone } from '@/lib/digits';
import { useLayout } from '@/lib/layout';
import type { TermProfile } from '@/lib/terminology';
import type { PanelProps } from '../components/BrandPanel';
import type { PreviewData } from '../components/LivePreview';
import { BigChips, BigInput, EnterBusiness, Msg, PhoneBig, phoneDigits, ShapeNote } from '../components/widgets';
import { useAfterCity, Wizard } from '../components/Wizard';
import { BUSINESS, HANDOVER, HOURS, PHONE, SHELL, WORKDAYS } from '../copy';
import { failureOf, citiesFromConfig, claimInvites, fetchBusinessTypes, sessionPersonName, suggestSlug, type BusinessType, type TeamInvite } from '../data/api';
import { ONB } from '../data/flow';
import { SHAPES, shapeOf } from '../data/shapes';
import { buildPreview, profileOf, typeShapeSentence, type PreviewInput } from '../preview';
import { onboardingStore, useOnboarding } from '../state';
import { useEnrollRun } from './useEnrollRun';
import { useShapeLists } from './shape/useShapeLists';
import { afterWriting, unscheduledRoute } from '../data/enroll';

/** The business step's last question goes to the shape's step 5 (seat has none: it writes and goes to the shape step). */
const STEP5_ROUTE = { hours: ONB.hours, workdays: ONB.workdays, handover: ONB.handover } as const;

export type BusinessScreenKey = 'name' | 'type' | 'city' | 'branches' | 'address' | 'bphone';

const SLUG_DEBOUNCE_MS = 400;

// ------------------------------------------------------------- account --

/**
 * The owner's name: the one they gave at «ما اسمك؟» (profile's business
 * identity), else the session's display name; null when only the phone is known.
 */
export function accountOwnerName(): string | null {
  return onboardingStore.get().ownerName.trim() || sessionPersonName();
}

/** The account's E.164 phone ('' when there is no session). */
export function accountPhone(): string {
  return sessionStore.getUser()?.phone ?? '';
}

// --------------------------------------------------------------- guard --

/**
 * The guard every business question shares: no session → the phone; the
 * business already exists → done (the answers are locked); a member → join;
 * an owner who already owns a business → straight into it (profile upserts
 * ONE business per owner — never walk the create step twice). Then the order
 * guard: a question needs the answers before it.
 */
export function useBusinessGuard(screen: BusinessScreenKey, writing = false): ReactElement | null {
  const s = useOnboarding();
  const authed = sessionStore.isAuthenticated();
  const needClaim = authed && s.intent === 'owner' && !s.createdId && s.memberships == null;
  const claim = useQuery({ queryKey: ['onboarding', 'claim'], queryFn: claimInvites, enabled: needClaim, retry: 0 });
  useEffect(() => {
    if (needClaim && claim.data) onboardingStore.set({ memberships: claim.data });
  }, [needClaim, claim.data]);

  if (!authed) return <Navigate to={ONB.phone} replace />;
  // `writing`: this question is creating the business right now (seat) — it navigates itself.
  if (s.createdId && !writing) {
    // Created but the scheduling PUT never succeeded (a failure, then a reload): back to the screen that writes it.
    const pending = unscheduledRoute();
    if (!pending) return <Navigate to={afterWriting()} replace />;
    if (!(screen === 'bphone' && pending === ONB.bphone)) return <Navigate to={pending} replace />;
  }
  if (s.intent === 'member') return <Navigate to={ONB.join} replace />;
  const known = s.memberships ?? claim.data ?? null;
  const owned = known?.find((m) => m.role === 'owner');
  if (owned) return <EnterBusiness id={owned.business_id} />;

  const hasName = !!s.draft.name.trim();
  const hasType = !!s.draft.category;
  const hasCity = !!s.draft.city.trim();
  if (screen !== 'name' && !hasName) return <Navigate to={ONB.name} replace />;
  if ((screen === 'city' || screen === 'branches' || screen === 'address' || screen === 'bphone') && !hasType) return <Navigate to={ONB.type} replace />;
  if ((screen === 'branches' || screen === 'address' || screen === 'bphone') && !hasCity) return <Navigate to={ONB.city} replace />;
  return null;
}

// --------------------------------------------------------------- panel --

const NO_INVITES: TeamInvite[] = [];

/** The public page as the answers so far draw it (rule 7: nothing invented), plus the term profile of the chosen type. */
export function useBusinessPreview(upTo: PreviewInput['upTo'], invites: TeamInvite[] = NO_INVITES): { profile: TermProfile; preview: PreviewData } {
  const { draft, type, hours, slug, branches, handover, createdId, sittingMin } = useOnboarding();
  // The shape step's server lists (the same queries the step holds) — on the shape step and done only.
  const lists = useShapeLists(shapeOf(type), createdId, upTo === 'shape' || upTo === 'done');
  return useMemo(
    () => ({
      profile: profileOf(type?.id),
      preview: buildPreview({
        name: draft.name,
        categoryId: type?.id ?? '',
        categoryLabel: type?.name_ar ?? '',
        city: draft.city,
        branch: branches.multi ? branches.firstName : '',
        area: draft.area,
        phone: draft.phone || accountPhone(),
        hours,
        shape: shapeOf(type),
        handover,
        slug,
        ownerName: accountOwnerName(),
        invites,
        lists: { ...lists, sittingMin },
        upTo,
      }),
    }),
    [draft, type, hours, slug, branches, handover, invites, lists, sittingMin, upTo],
  );
}

/** The brand panel in its preview phase, filled up to the question being asked. */
export function useBusinessPanel(upTo: PreviewInput['upTo']): PanelProps {
  const { profile, preview } = useBusinessPreview(upTo);
  return useMemo(() => ({ phase: 'preview', profile, preview }), [profile, preview]);
}

/** The server's refusal of this field, handed over by the hours screen (read once). */
function useFieldErrorFromState(): string | null {
  const location = useLocation();
  const st = location.state as { fieldError?: unknown } | null | undefined;
  const [initial] = useState<string | null>(() => (typeof st?.fieldError === 'string' && st.fieldError ? st.fieldError : null));
  return initial;
}

export function ContinueAction({ mobile, loading, label = SHELL.continue }: { mobile: boolean; loading?: boolean; label?: string }) {
  return (
    <PrimaryAction type="submit" size="lg" icon={ArrowLeft} block={mobile} loading={loading}>
      {label}
    </PrimaryAction>
  );
}

const PHONE_INVALID: ReactNode = (
  <>
    {PHONE.invalid}
    <bdi dir="ltr" className="ltr">
      {PHONE.mask}
    </bdi>
  </>
);

// ---------------------------------------------------------------- name --

export function BusinessNameScreen() {
  const guard = useBusinessGuard('name');
  const navigate = useNavigate();
  const mobile = useLayout() === 'phone';
  const { draft, type } = useOnboarding();
  const base = useBusinessPanel('name');
  // Board 25 v2: the name types in on this step only (a name that is already there when the step opens).
  const panel = useMemo<PanelProps>(() => ({ ...base, preview: base.preview && { ...base.preview, typeName: true } }), [base]);
  const serverError = useFieldErrorFromState();
  const [error, setError] = useState<string | null>(serverError);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seq = useRef(0);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      seq.current += 1;
    },
    [],
  );

  if (guard) return guard;
  const profile = profileOf(type?.id);

  const onChange = (v: string) => {
    onboardingStore.setDraft({ name: v });
    setError(null);
    if (timer.current) clearTimeout(timer.current);
    const name = v.trim();
    const my = ++seq.current;
    if (name.length < 2) {
      onboardingStore.set({ slug: null });
      return;
    }
    timer.current = setTimeout(() => {
      const { draft: d } = onboardingStore.get();
      suggestSlug(name, d.city, d.category)
        .then((slug) => {
          if (my === seq.current) onboardingStore.set({ slug });
        })
        .catch(() => {
          /* the handle is a suggestion — «…» stays until profile answers */
        });
    }, SLUG_DEBOUNCE_MS);
  };

  const submit = () => {
    if (!draft.name.trim()) {
      setError(BUSINESS.nameRequired);
      return;
    }
    navigate(ONB.type);
  };

  return (
    <Wizard
      screen="name"
      testId="onb-business-name"
      state={error ? 'validation' : 'default'}
      question={BUSINESS.nameTitle}
      sub={BUSINESS.nameSub(profile)}
      primary={<ContinueAction mobile={mobile} />}
      enterHint
      peek={BUSINESS.peekType}
      onSubmit={submit}
      panel={panel}
    >
      <BigInput autoFocus data-testid="onb-name" maxLength={60} placeholder={BUSINESS.namePlaceholder} value={draft.name} onChange={onChange} error={error} autoComplete="organization" />
    </Wizard>
  );
}

// ---------------------------------------------------------------- type --

const ALL_TYPES_ID = '__all';

export function BusinessTypeScreen() {
  const guard = useBusinessGuard('type');
  const navigate = useNavigate();
  const mobile = useLayout() === 'phone';
  const { draft, type } = useOnboarding();
  const panel = useBusinessPanel('type');
  const authed = sessionStore.isAuthenticated();
  const types = useQuery({ queryKey: ['onboarding', 'types'], queryFn: fetchBusinessTypes, enabled: authed, staleTime: 5 * 60_000, retry: 1 });
  const serverError = useFieldErrorFromState();
  const [error, setError] = useState<string | null>(serverError);
  const [search, setSearch] = useState(false);
  const [query, setQuery] = useState('');

  if (guard) return guard;

  const list = types.data ?? [];
  const q = query.trim();
  const shown = search && q ? list.filter((t) => t.name_ar.includes(q) || t.id.includes(q.toLowerCase())) : list;
  const selected = list.find((t) => t.id === draft.category) ?? type;
  const shape = shapeOf(selected);
  const word = shape ? SHAPES[shape].word : null;

  const pick = (id: string) => {
    setError(null);
    if (id === ALL_TYPES_ID) {
      setSearch(true);
      return;
    }
    const t: BusinessType | undefined = list.find((x) => x.id === id);
    if (!t) return;
    onboardingStore.set({ type: t });
    onboardingStore.setDraft({ category: t.id });
  };

  const submit = () => {
    if (!selected) {
      setError(BUSINESS.typeRequired);
      return;
    }
    navigate(ONB.city);
  };

  const state = types.isPending ? 'loading' : types.isError ? 'error' : error ? 'validation' : search ? 'search' : 'default';
  const items = [...shown.map((t) => ({ id: t.id, label: t.name_ar })), { id: ALL_TYPES_ID, label: BUSINESS.allTypes, search: true }];
  const value = selected?.id ?? (search ? ALL_TYPES_ID : null);

  return (
    <Wizard
      screen="type"
      testId="onb-business-type"
      state={state}
      question={BUSINESS.typeTitle}
      sub={BUSINESS.typeSub}
      primary={<ContinueAction mobile={mobile} />}
      enterHint
      peek={BUSINESS.peekCity}
      onSubmit={submit}
      error={types.isError ? { ...failureOf(types.error), onRetry: () => void types.refetch() } : null}
      panel={panel}
    >
      {types.isPending ? (
        <div className="onb-chips" aria-busy="true">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} width={96} height={48} />
          ))}
        </div>
      ) : (
        <>
          {search && (
            <TextInput icon={Search} placeholder={BUSINESS.searchType} aria-label={BUSINESS.searchType} data-testid="onb-type-search" autoFocus value={query} onChange={(e) => setQuery(e.target.value)} />
          )}
          <BigChips data-testid="onb-types" label={BUSINESS.typeTitle} items={items} value={value} onChange={pick} />
          {search && q && shown.length === 0 && <p className="e-field-hint">{BUSINESS.noMatch}</p>}
          {selected && shape && word && (
            <ShapeNote testId="onb-shape" before={BUSINESS.shapeBefore} word={word} sentence={typeShapeSentence(shape, selected.id)} later={BUSINESS.shapeLater} />
          )}
          {error && <Msg testId="onb-type-error">{error}</Msg>}
        </>
      )}
    </Wizard>
  );
}

// ---------------------------------------------------------------- city --

const OTHER_CITY_ID = '__other';

export function BusinessCityScreen() {
  const guard = useBusinessGuard('city');
  const navigate = useNavigate();
  const mobile = useLayout() === 'phone';
  const { draft } = useOnboarding();
  const panel = useBusinessPanel('city');
  const next = useAfterCity();
  // «متابعة» before the platform config answered: wait for it (the branches question depends on it).
  const [pending, setPending] = useState(false);
  useEffect(() => {
    if (pending && next.known) navigate(next.to);
  }, [pending, next.known, next.to, navigate]);
  const config = useConfigQuery();
  const cities = useMemo(() => citiesFromConfig(config.data), [config.data]);
  const serverError = useFieldErrorFromState();
  const [error, setError] = useState<string | null>(serverError);
  const listed = cities.some((c) => c.name === draft.city);
  const [otherPicked, setOtherPicked] = useState(false);
  // «مدينة أخرى» was pressed, or the answer is a city the platform does not list (a reload keeps it).
  const other = otherPicked || (!!draft.city.trim() && !listed);

  if (guard) return guard;

  const pick = (id: string) => {
    setError(null);
    if (id === OTHER_CITY_ID) {
      setOtherPicked(true);
      if (listed) onboardingStore.setDraft({ city: '' });
      return;
    }
    setOtherPicked(false);
    onboardingStore.setDraft({ city: id });
  };

  const submit = () => {
    if (!draft.city.trim()) {
      setError(BUSINESS.cityRequired);
      return;
    }
    if (!next.known) {
      setPending(true);
      return;
    }
    navigate(next.to);
  };

  const hasCities = cities.length > 0;
  const freeText = !hasCities || other;
  const value = listed ? draft.city : other ? OTHER_CITY_ID : null;
  const state = error ? 'validation' : hasCities && other ? 'other' : 'default';

  return (
    <Wizard
      screen="city"
      testId="onb-business-city"
      state={state}
      question={BUSINESS.cityTitle}
      sub={BUSINESS.citySub}
      primary={<ContinueAction mobile={mobile} />}
      enterHint
      peek={next.peek || BUSINESS.peekAddress}
      onSubmit={submit}
      panel={panel}
    >
      {hasCities && (
        <BigChips
          data-testid="onb-cities"
          label={BUSINESS.cityTitle}
          items={[...cities.map((c) => ({ id: c.name, label: c.name })), { id: OTHER_CITY_ID, label: BUSINESS.otherCity, search: true }]}
          value={value}
          onChange={pick}
        />
      )}
      {freeText && (
        <BigInput
          autoFocus
          data-testid="onb-city"
          placeholder={BUSINESS.cityPlaceholder}
          maxLength={60}
          value={listed ? '' : draft.city}
          onChange={(v) => {
            setError(null);
            onboardingStore.setDraft({ city: v });
          }}
          autoComplete="address-level2"
        />
      )}
      {error && <Msg testId="onb-city-error">{error}</Msg>}
    </Wizard>
  );
}

// ------------------------------------------------------------- address --

export function BusinessAddressScreen() {
  const guard = useBusinessGuard('address');
  const navigate = useNavigate();
  const mobile = useLayout() === 'phone';
  const { draft } = useOnboarding();
  const panel = useBusinessPanel('address');
  const serverError = useFieldErrorFromState();
  const [error, setError] = useState<string | null>(serverError);

  if (guard) return guard;

  const skip = () => {
    onboardingStore.setDraft({ area: '' });
    navigate(ONB.bphone);
  };

  return (
    <Wizard
      screen="address"
      testId="onb-business-address"
      state={error ? 'validation' : 'default'}
      question={BUSINESS.addressTitle}
      sub={BUSINESS.addressSub}
      primary={<ContinueAction mobile={mobile} />}
      enterHint
      tertiary={
        <button type="button" className="onb-tert" data-testid="onb-skip-address" onClick={skip}>
          {BUSINESS.skip}
        </button>
      }
      peek={BUSINESS.peekPhone}
      onSubmit={() => navigate(ONB.bphone)}
      panel={panel}
    >
      <BigInput
        autoFocus
        data-testid="onb-area"
        placeholder={BUSINESS.addressPlaceholder}
        maxLength={120}
        value={draft.area}
        onChange={(v) => {
          setError(null);
          onboardingStore.setDraft({ area: v });
        }}
        error={error}
        autoComplete="street-address"
      />
    </Wizard>
  );
}

// --------------------------------------------------------------- bphone --

export function BusinessPhoneScreen() {
  // Seat: no step 5 — this question writes the business (saving state, the shell's error card).
  const enroll = useEnrollRun();
  const guard = useBusinessGuard('bphone', enroll.phase !== 'idle');
  const navigate = useNavigate();
  const mobile = useLayout() === 'phone';
  const { draft, type } = useOnboarding();
  const panel = useBusinessPanel('bphone');
  const serverError = useFieldErrorFromState();
  const [error, setError] = useState<ReactNode>(serverError);
  const step5 = SHAPES[shapeOf(type) ?? 'staff'].step5;

  // Prefilled with the account's number: written into the draft once when empty.
  // After that the field shows the draft only, so erasing it does not snap back to the account's number.
  const prefilled = useRef(!!onboardingStore.get().draft.phone);
  useEffect(() => {
    if (prefilled.current) return;
    prefilled.current = true;
    const mine = phoneDigits(accountPhone());
    if (mine) onboardingStore.setDraft({ phone: mine });
  }, []);

  if (guard) return guard;
  const profile = profileOf(type?.id);
  const digits = draft.phone || prefilled.current ? phoneDigits(draft.phone) : phoneDigits(accountPhone());

  const submit = () => {
    if (enroll.phase === 'saving') return;
    if (!normalizeIraqiPhone(`0${digits}`)) {
      setError(PHONE_INVALID);
      return;
    }
    if (step5 === 'none') {
      void enroll.run();
      return;
    }
    navigate(STEP5_ROUTE[step5]);
  };
  const saving = enroll.phase === 'saving';
  const peek = step5 === 'workdays' ? WORKDAYS.title : step5 === 'handover' ? HANDOVER.title(profile) : step5 === 'hours' ? HOURS.title(profile) : undefined;

  return (
    <Wizard
      screen="bphone"
      testId="onb-business-phone"
      state={saving ? 'saving' : error ? 'invalid' : 'default'}
      question={BUSINESS.phoneTitle}
      sub={BUSINESS.phoneSub(profile)}
      primary={<ContinueAction mobile={mobile} loading={saving} />}
      enterHint
      peek={peek}
      onSubmit={submit}
      error={enroll.failure ? { ...enroll.failure, onRetry: () => void enroll.run() } : null}
      panel={panel}
    >
      <PhoneBig
        data-testid="onb-bphone-input"
        aria-label={BUSINESS.phoneTitle}
        autoFocus
        value={digits}
        error={error}
        onChange={(d) => {
          setError(null);
          onboardingStore.setDraft({ phone: d });
        }}
      />
    </Wizard>
  );
}
